import type { BombView, PlayerView, Snapshot } from "../shared/protocol.js";

// Snapshots are drawn a little in the past (DELAY behind the newest), moving smoothly
// between the two that bracket the playback time. Network jitter then never shows: a late
// snapshot just eats into the buffer instead of freezing everyone for a frame.
// Times are the server's game clock (steps * DT), so uneven arrival doesn't matter.
// No DOM in here, so test/playback.test.ts can check smoothness.
const SERVER_DT = 1 / 60;
export const DELAY = 0.075; // seconds: a bit over two snapshots
const BUFFER = 8;
export const FAST_RATE = 4; // keep in step with the server's FAST_FACTOR

export interface Frame {
  s: Snapshot; // snapshot to draw the arena from
  players: PlayerView[];
  bombs: BombView[];
  age: number; // seconds of game time since `s`
}

export class Playback {
  private buffer: { t: number; s: Snapshot }[] = [];
  private playT = 0; // game clock being drawn

  push(s: Snapshot) {
    const t = s.tick * SERVER_DT;
    const last = this.buffer[this.buffer.length - 1];
    // A new round (clock restarts) or no game: start the buffer over.
    if (!last || t < last.t || s.round !== last.s.round || s.phase === "lobby") {
      this.buffer = [];
      this.playT = t;
    }
    this.buffer.push({ t, s });
    if (this.buffer.length > BUFFER) this.buffer.shift();
  }

  clear() {
    this.buffer = [];
  }

  // Advance by `dt` seconds of wall time and return what to draw.
  frame(dt: number): Frame | null {
    const buffer = this.buffer;
    if (!buffer.length) return null;
    const newest = buffer[buffer.length - 1];
    const rate = newest.s.fast ? FAST_RATE : 1;
    // Advance at the game's speed, nudged toward "newest minus delay" so it neither runs
    // dry nor drifts behind; jump if far off. Small drift is left alone: steadier steps.
    const target = newest.t - DELAY * rate;
    this.playT += dt * rate;
    const err = target - this.playT;
    if (Math.abs(err) > 0.3 * rate) this.playT = target;
    else if (Math.abs(err) > 0.02 * rate) this.playT += err * Math.min(1, dt * 2);
    this.playT = Math.min(this.playT, newest.t);

    let i = buffer.length - 1;
    while (i > 0 && buffer[i].t > this.playT) i--;
    const a = buffer[i];
    const b = buffer[Math.min(i + 1, buffer.length - 1)];
    const f = b.t > a.t ? Math.max(0, Math.min(1, (this.playT - a.t) / (b.t - a.t))) : 0;
    return {
      s: a.s,
      players: lerpById(a.s.players, b.s.players, (p) => p.id, f),
      bombs: lerpById(a.s.bombs, b.s.bombs, (o) => o.id, f),
      age: Math.max(0, this.playT - a.t),
    };
  }
}

function lerpById<T extends { x: number; y: number }>(a: T[], b: T[], key: (o: T) => string | number, f: number): T[] {
  const prev = new Map(a.map((o) => [key(o), o]));
  return b.map((o) => {
    const p = prev.get(key(o));
    // Big jumps (wrap-arounds, a new round) go straight there.
    if (!p || Math.abs(p.x - o.x) + Math.abs(p.y - o.y) > 48) return o;
    return { ...o, x: p.x + (o.x - p.x) * f, y: p.y + (o.y - p.y) * f };
  });
}

// Only touch the DOM when something changed: rebuilding the HUD 30 times a second makes the
// browser restyle and relayout the page every snapshot.
const shown = new WeakMap<object, string>();
export function setHtml(el: { innerHTML: string }, html: string) {
  if (shown.get(el) === html) return;
  shown.set(el, html);
  el.innerHTML = html;
}
export function setText(el: { textContent: string | null }, text: string) {
  if (el.textContent !== text) el.textContent = text;
}
